import { Immutable } from "immer";
import React from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

type IProps = Immutable<{
    // iovation "snare" script URL. When empty, the snare is skipped while the
    // IGLOO config and loader.js still run.
    iovationUrl: string;
    // Base URL for site static assets; loader.js is resolved beneath
    // `${staticBase}js/iovation/`.
    staticBase: string;
}>;

// Injects the iovation fraud-fingerprinting scripts imperatively from useEffect
// (post-hydration). On a full-document React page React owns <head>, and a
// declarative `<script async src>` snare would be hoisted into <head> by React
// 19, where it could execute before the body-level IGLOO config on a warm cache
// (silently degrading fingerprinting) or be paired against a GTM-injected
// foreign <script> and trip a hydration mismatch (see PageSkeleton's invariant).
// Appending the scripts after hydration commits sidesteps both.
export const IovationScripts = (props: IProps): React.ReactNode => {
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (!win || !doc) {
            return;
        }
        // IGLOO config must exist before snare.js runs, and snare must run
        // before loader.js. Setting IGLOO synchronously here guarantees it
        // precedes either appended script. async=false on both external
        // scripts preserves their relative execution order (dynamically
        // created scripts are async by default, so append order alone would
        // not).
        win.IGLOO = win.IGLOO || {
            enable_rip: true,
            enable_flash: false,
            install_flash: false,
            loader: { version: "general5", fp_static: false },
        };
        let snare: HTMLScriptElement | null = null;
        if (props.iovationUrl) {
            snare = doc.createElement("script");
            snare.async = false;
            snare.src = props.iovationUrl;
            doc.body.appendChild(snare);
        }
        const loader = doc.createElement("script");
        loader.async = false;
        loader.src = `${props.staticBase}js/iovation/loader.js`;
        doc.body.appendChild(loader);
        // Remove the appended nodes if the effect re-runs (prop change) or under
        // StrictMode's dev double-invoke, so each run starts from a clean body
        // and the script tags do not accumulate. (This cannot undo a payload
        // that already executed; it only prevents duplicate tags.)
        return () => {
            snare?.remove();
            loader.remove();
        };
    }, [props.iovationUrl, props.staticBase]);
    return null;
};
